import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ShieldCheck, ChartLineUp, FileText } from "@phosphor-icons/react";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [localErr, setLocalErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setLocalErr("");
    try {
      const loggedUser = await login(email, password);
      navigate(loggedUser?.mustChangePassword ? "/change-password" : "/app");
    } catch (err) {
      setLocalErr(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr] aurora-mesh" data-testid="login-page">
      {/* Left immersive */}
      <div className="hidden lg:flex relative items-end p-12 overflow-hidden aurora-mesh-dark">
        {/* Animated blobs */}
        <div className="absolute top-20 right-10 h-72 w-72 rounded-full bg-[var(--do-accent)] opacity-50 blur-[100px] au-float" />
        <div className="absolute bottom-20 left-10 h-80 w-80 rounded-full bg-[var(--do-accent-soft)]0 opacity-30 blur-[120px] au-float" style={{ animationDelay: "1s" }} />
        <div className="absolute top-1/3 left-1/2 h-64 w-64 rounded-full bg-[var(--do-accent-2)] opacity-25 blur-[100px] au-float" style={{ animationDelay: "2s" }} />

        <Link to="/" className="absolute top-10 left-12 flex items-center gap-2.5 text-[var(--do-on-accent)] hover:opacity-90 transition-opacity" data-testid="login-logo-home">
          <div className="h-9 w-9 rounded-[10px] flex items-center justify-center"
               style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.7))" }}>
            <span className="text-[var(--do-ink)] font-display text-[13px] tracking-[0.18em]">A</span>
          </div>
          <div>
            <div className="font-heading font-bold text-sm leading-tight">AlmaCRM</div>
          </div>
        </Link>

        <div className="relative text-[var(--do-on-accent)] max-w-md au-rise">
          <h2 className="font-display text-[52px] leading-[0.96] tracking-tight">
            Vos prospects<br />
            <span className="text-gradient-pink">brûlent</span><br />
            d'envie.
          </h2>
          <p className="mt-6 text-[var(--do-on-accent)] text-sm max-w-sm leading-relaxed">
            Centralisez vos documents, partagez avec vos prospects et mesurez chaque seconde d'attention. Une plateforme. Zéro friction.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
            {[
              { icon: FileText, value: "100%", label: "Tracking docs" },
              { icon: ShieldCheck, value: "TLS 1.3", label: "Chiffrement" },
              { icon: ChartLineUp, value: "<1s", label: "Live data" },
            ].map((s) => (
              <div key={s.label} className="border border-[var(--do-border)] bg-[var(--do-bg-elev)] backdrop-blur-xl rounded-[12px] p-3">
                <s.icon size={16} className="text-[var(--do-on-accent)]" />
                <div className="font-mono-stat text-sm font-bold mt-1.5">{s.value}</div>
                <div className="text-[10px] text-[var(--do-on-accent)] uppercase tracking-wider mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 lg:p-16 relative overflow-hidden">
        <div className="absolute top-10 right-10 h-40 w-40 rounded-full bg-[var(--do-accent-2)] opacity-30 blur-[80px]" />
        <div className="absolute bottom-10 left-10 h-32 w-32 rounded-full bg-[var(--do-accent-2)] opacity-30 blur-[60px]" />

        <div className="w-full max-w-sm relative au-rise">
          <div className="label-eyebrow mb-3">Connexion</div>
          <h1 className="font-display text-4xl tracking-tight mb-2">Bon retour.</h1>
          <p className="text-[var(--do-muted)] text-sm mb-8">Accédez à votre espace documentaire sécurisé.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full px-4 py-3 border border-[var(--do-border)] rounded-[12px] text-sm"
                placeholder="vous@entreprise.fr"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Mot de passe</label>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-4 py-3 border border-[var(--do-border)] rounded-[12px] text-sm"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-[var(--do-accent)] font-semibold hover:text-[var(--do-ink)]" data-testid="forgot-password-link">
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            {(localErr || error) && (
              <div className="text-sm text-[var(--do-accent)] border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2.5 rounded-[10px]" data-testid="login-error">
                {localErr || error}
              </div>
            )}

            <button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="au-btn-primary w-full px-4 py-3.5 text-sm inline-flex items-center justify-center gap-2"
            >
              {loading ? "Connexion…" : <>Se connecter <ArrowRight size={14} weight="bold" /></>}
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
